import { alpha, Box, CardMedia, Grid, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { getAssetsBaseUrl } from "../../../config/assetsUrl";

type Motorcycle = {
    name: string;
    image: string;
};

const motorcycles: Motorcycle[] = [
    { name: "MR Pro 300i (2026)", image: "mr-pro-300i-2026.png" },
    { name: "MR Racing 300i (2026)", image: "mr-racing-300i-2026.png" },
    { name: "MR Ranger 200-300", image: "mr-ranger-200-300.png" },
    { name: "Sherco", image: "sherco.png" },
];

const EquipmentPage: React.FC = () => {
    const { t } = useTranslation();
    const paragraphs = t("equipment.paragraphs", { returnObjects: true }) as string[];
    const highlights = t("equipment.highlights.items", { returnObjects: true }) as string[];

    useEffect(() => {
        window?.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>{t("seo.equipment.title")}</title>
                <meta name="description" content={t("seo.equipment.description")} />
            </Helmet>
            <Box px={1} mt={{ xs: 8, sm: 10 }} mb={{ xs: 4, sm: 6 }}>
                <Box mb={{ xs: 4, md: 5 }}>
                    <Typography variant="h3" fontWeight={800} mb={2}>
                        {t("equipment.title")}
                    </Typography>
                    {paragraphs.map((paragraph, index) => (
                        <Typography key={`equipment-paragraph-${index}`} color="text.primary" mb={2}>
                            {paragraph}
                        </Typography>
                    ))}
                    <Typography variant="h5" fontWeight={700} mt={4} mb={2}>
                        {t("equipment.highlights.title")}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {highlights.map((item, index) => (
                            <Typography component="li" key={`equipment-highlight-${index}`} color="text.primary" mb={1}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                </Box>
                <Grid container spacing={3}>
                    {motorcycles.map((motorcycle) => (
                        <Grid item xs={12} sm={6} md={6} key={motorcycle.image}>
                            <Box
                                className="equipment-card"
                                sx={(theme) => ({
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    backgroundColor: alpha(theme.palette.background.paper, 0),
                                    // border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                                    boxShadow: "none",
                                    transition:
                                        "box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                    transform: "translateY(0)",
                                    "&:hover": {
                                        boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.35)}`,
                                        transform: "translateY(-6px)",
                                    },
                                    "&:hover .equipment-image": {
                                        transform: "scale(1.08) rotate(-3deg)",
                                    },
                                    "&:hover .equipment-title": {
                                        transform: "scale(1.08)",
                                    },
                                })}
                            >
                                <Box sx={{ overflow: "hidden" }}>
                                    <CardMedia
                                        component="img"
                                        image={`${getAssetsBaseUrl()}/images/equipment/${motorcycle.image}`}
                                        alt={motorcycle.name}
                                        className="equipment-image"
                                        sx={{
                                            height: { xs: 300, sm: 360, md: 420 },
                                            objectFit: "cover",
                                            display: "block",
                                            transition:
                                                "transform 0.45s cubic-bezier(0.34, 1.2, 0.64, 1)",
                                            transform: "scale(1) rotate(0deg)",
                                            transformOrigin: "center center",
                                        }}
                                    />
                                </Box>
                                <Typography
                                    className="equipment-title"
                                    variant="h6"
                                    fontWeight={700}
                                    textAlign="center"
                                    sx={{
                                        py: 1.75,
                                        px: 1,
                                        display: "block",
                                        transformOrigin: "center center",
                                        transition:
                                            "transform 0.4s cubic-bezier(0.34, 1.2, 0.64, 1)",
                                        transform: "scale(1)",
                                    }}
                                >
                                    {motorcycle.name}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    );
};

export default EquipmentPage;
